import { useEffect, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Loading, ErrorNote } from '../components/ui.js';
import { useMediaQuery } from '../lib/useMediaQuery.js';
import { useLayoutEditor } from './layoutEditor/useLayoutEditor.js';
import { DesktopEditor } from './layoutEditor/DesktopEditor.js';
import { MobileEditor } from './layoutEditor/MobileEditor.js';

/** Matches the stylesheet's single mobile breakpoint. */
const MOBILE_QUERY = '(max-width: 900px)';

export function LayoutEditorScreen(): ReactNode {
  const { id } = useParams();
  const ed = useLayoutEditor(id);
  const mobile = useMediaQuery(MOBILE_QUERY);
  const { undo, redo } = ed.history;

  // Ctrl/Cmd+Z undoes, Ctrl+Shift+Z / Ctrl+Y redoes — except while typing,
  // where the field's own undo is the one people expect.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName))) return;
      const key = e.key.toLowerCase();
      if (key === 'z' && !e.shiftKey) undo();
      else if ((key === 'z' && e.shiftKey) || key === 'y') redo();
      else return;
      e.preventDefault();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  if (id && ed.existing.loading && !ed.existing.data) return <Loading what="layout" />;
  if (ed.existing.error) return <ErrorNote error={ed.existing.error} />;
  if (!ed.doc) return <Loading />;

  return mobile ? <MobileEditor ed={ed} /> : <DesktopEditor ed={ed} />;
}
